import { inject, Injectable, Type } from "@angular/core";
import { ActivatedRoute, Router, UrlTree } from "@angular/router";
import { TaxesRouteNames } from "@modules/taxes/domain/entities/taxes.model";
import { ROUTER_OUTLET_NAME } from "@modules/+new/shared/universal-modal/entities/magic";
import { TAXES } from "@helpers/constants";
import { UniversalModalService } from "@modules/+new/shared/universal-modal/universal-modal.service";
import { ModalCloseReason } from "@modules/+new/shared/universal-modal/entities/modal-close-reason.enum";
import { TaxesQueryParamsCollection } from "@modules/taxes/domain/entities/taxes-navigation.model";
import { Observable, take } from "rxjs";
import { Offer } from "@models";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { ModalService } from "@core-services/modal.service";
import { TradeSwapComponent } from "@modules/trade/modals/trade-swap-modal/trade-swap.component";
import { TradeSellComponent } from "@modules/trade/modals/trade-sell-modal/trade-sell.component";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { isSafari } from "@helpers/utils";
import {
	LimitOrderQueryParamName,
	LimitOrderRouteName
} from "@modules/limit-orders/interfaces/limit-orders-route.interface";
import { AccountService } from "@modules/account/services/account.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { CheckoutNavigationService } from "@modules/checkout/services/checkout-navigation.service";
import { MarginTradingRouteName } from "@modules/margin-trading/entities/margin-trading.model";

@Injectable({
	providedIn: "root"
})
export class TaxesNavigationService {
	private readonly universalModalService = inject(UniversalModalService);
	private readonly router = inject(Router);
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly modalService = inject(ModalService);
	private readonly walletListService = inject(TradableWalletListService);
	private readonly accountService = inject(AccountService);
	private readonly fiatWalletService = inject(FiatWalletService);
	private checkoutNavigationService = inject(CheckoutNavigationService);

	public gotoByNavigationUrl(path: TaxesRouteNames, queryParams?: Partial<TaxesQueryParamsCollection>): void {
		const urlTree = this.gotoByUrlTree(path, queryParams);
		this.router.navigateByUrl(urlTree);
	}

	public gotoByNavigationUrlGeneric<P>(path: TaxesRouteNames, queryParams?: Partial<P>): void {
		const urlTree = this.gotoByUrlTree(path, queryParams);
		this.router.navigateByUrl(urlTree);
	}

	private gotoByUrlTree(path: TaxesRouteNames, queryParams?: Partial<TaxesQueryParamsCollection>): UrlTree {
		return this.router.createUrlTree([{ outlets: { [ROUTER_OUTLET_NAME]: [TAXES, path] } }], { queryParams });
	}

	public async closeModal(): Promise<void> {
		await this.universalModalService.close(ModalCloseReason.close);
	}

	// Close modal and flush out query params
	public async closeNavigatedModal(): Promise<void> {
		const urlTree = this.router.createUrlTree([{ outlets: { [ROUTER_OUTLET_NAME]: null } }]);
		await this.closeModal();
		await this.router.navigateByUrl(urlTree);
	}

	public onBackFromConfirmation(navigationFrom: string): void {
		const path = Object.values(TaxesRouteNames).find((val) => val === navigationFrom);
		if (path) {
			this.routeParamsReader()
				.pipe(take(1))
				.subscribe({
					next: async (query) => {
						const _query = query.fromTradeFlow ? query : null;
						this.gotoByNavigationUrl(TaxesRouteNames.CryptoDeclaration, _query);
					}
				});
		}
	}

	routeParamsReader(): Observable<Partial<TaxesQueryParamsCollection>> {
		return this.activatedRoute.queryParams;
	}

	public updateQueryParamsAndNavigate(
		path: TaxesRouteNames,
		queryParams?: Partial<TaxesQueryParamsCollection>
	): void {
		const route = { outlets: { [ROUTER_OUTLET_NAME]: [TAXES, path] } };

		this.router.navigate([route], {
			queryParams,
			queryParamsHandling: "merge"
		});
	}

	gotoToHomeUrlTree(): UrlTree {
		return this.router.createUrlTree(["/home"]);
	}

	getQueryParams$(): Observable<Partial<TaxesQueryParamsCollection>> {
		return this.activatedRoute.queryParams;
	}

	returnToTradeFlow(): void {
		this.routeParamsReader()
			.pipe(take(1))
			.subscribe({
				next: async (query) => {
					this.closeModal();
					if (query.fromTradeFlow === TaxesRouteNames.SellTrade) {
						this.showTradeModal(TradeSellComponent, TRADE_TYPE.SELL);
					} else if (query.fromTradeFlow === TaxesRouteNames.SwapTrade) {
						this.showTradeModal(TradeSwapComponent);
					} else if (query.fromTradeFlow === TaxesRouteNames.MarginTrading) {
						this.checkoutNavigationService.gotoByNavigation(
							MarginTradingRouteName.MarginTradingBuyAmount,
							query
						);
					}
				}
			});
	}

	private showTradeModal(component: Type<unknown>, type?: string): void {
		const data = new Offer();
		if (type === TRADE_TYPE.SELL) {
			data.setType(TRADE_TYPE.SELL);
		}

		if (!isSafari()) {
			this.modalService.pop();
			const modal = this.modalService.create(component, { data });
			modal.present();
		} else {
			// Pop first, then create new popup
			this.modalService.pop();
			setTimeout(() => {
				const modal = this.modalService.create(component, { data });
				modal.present();
			}, 200);
		}
	}

	public continueTradeFlow(): void {
		this.routeParamsReader()
			.pipe(take(1))
			.subscribe({
				next: async (query) => {
					if (query.fromTradeFlow === TaxesRouteNames.SellTrade) {
						const offer = new Offer();
						offer.setDirectTradableWalletOffer(true);
						offer.setType(TRADE_TYPE.SELL);
						offer.setCurrencyId(query.assetId);
						offer.setWalletId(query.assetWalletId);
						this.closeModal();
						this.modalService.create(TradeSellComponent, { data: offer, fromTWFlow: true }).present();
					} else if (query.fromTradeFlow === TaxesRouteNames.SwapTrade) {
						const leftWallet = this.walletListService.getItemById(query.LeftSelectionWalletId);
						const rightWallet = this.walletListService.getItemById(query.RightSelectionWalletId);
						this.modalService
							.create(TradeSwapComponent, {
								fromTWFlow: true,
								previousLeftAssetId: leftWallet.getCurrencyId(),
								previousRightAssetId: rightWallet.getCurrencyId(),
								externalLaunchOptions: {
									id: leftWallet.getCurrencyId(),
									id2: rightWallet.getCurrencyId(),
									wallet: leftWallet,
									usedFor: "left",
									wallet2: rightWallet,
									usedFor2: "right"
								}
							})
							.present();
						this.closeModal();
					} else if (query.fromTradeFlow === TaxesRouteNames.SellOrderTrade) {
						// Can't use order entry service due to circular dependency => open the amount screen via navigation
						const queryParams = {
							[LimitOrderQueryParamName.WalletId]: query.assetWalletId,
							[LimitOrderQueryParamName.AssetId]: query.assetId,
							[LimitOrderQueryParamName.FiatId]: this.accountService.getDefaultFiat().getId(),
							[LimitOrderQueryParamName.FiatWalletId]: this.fiatWalletService
								.getWalletByFiatId(this.accountService.getDefaultFiat().getId())
								.getId(),
							[LimitOrderQueryParamName.Amount]: "0",
							[LimitOrderQueryParamName.TargetPrice]: "0"
						};
						this.router.navigate(
							[
								{
									outlets: {
										[ROUTER_OUTLET_NAME]: [
											LimitOrderRouteName.LimitOrderList,
											LimitOrderRouteName.LimitOrderSellAmount
										]
									}
								}
							],
							{
								queryParams
							}
						);
					} else if (query.fromTradeFlow === TaxesRouteNames.MarginTrading) {
						this.checkoutNavigationService.gotoByNavigation(
							MarginTradingRouteName.MarginTradingBuySummary,
							query
						);
					}
				}
			});
	}
}
